import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, LineChart, Contact, TrendingUp, DoorOpen,
  CalendarDays, CreditCard, History, FileSignature, Building2, Video, LayoutGrid, DoorClosed, Music2, AlertTriangle, IdCard,
  PartyPopper,
} from "lucide-react";
import api from "../lib/api";
import Header from "../components/dashboard/Header";
import CameraWall from "../components/CameraWall";
import LocationBoards from "../components/LocationBoards";
import DoorsPanel from "../components/DoorsPanel";
import MusicPanel from "../components/MusicPanel";
import PhotoFallback from "../components/PhotoFallback";
import NeedsAttention from "../components/NeedsAttention";
import LocationMemberSearch from "../components/LocationMemberSearch";
import { AvailabilityPanel } from "../components/AvailabilityPanel";
import { ProgrammingCalendar } from "../components/programming/ProgrammingCalendar";
import { LocationWaitlist, LocationMoveOuts } from "../components/LocationSalesPanels";
import { fmtCurrency, fmtPeriod } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

function StatCard({ label, value, tone, testid }) {
  return (
    <div className="bg-brand-surface border border-brand-border rounded-xl p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary">{label}</p>
      <p className={`mt-2 text-2xl font-serif tracking-tight ${tone || "text-brand-ink"}`}>{value}</p>
    </div>
  );
}

const TABS = [
  ["overview", "Overview", Building2], ["members", "Members", IdCard], ["sales", "Sales", TrendingUp],
  ["events", "Events", PartyPopper], ["operations", "Operations", DoorClosed], ["tasks", "Tasks", LayoutGrid],
];

function Section({ icon: Icon, title, sub, action, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-5 w-5 text-brand-ink shrink-0" />
          <h2 className="text-lg text-brand-ink font-medium">{title}</h2>
          {sub && <span className="text-xs text-brand-tertiary truncate hidden sm:inline">{sub}</span>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function ModuleTile({ to, icon: Icon, label, desc, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="group bg-brand-surface border border-brand-border rounded-xl p-5 flex items-center gap-4 transition-all duration-200 hover:border-brand-primary hover:shadow-md hover:-translate-y-0.5">
      <span className="h-11 w-11 rounded-lg bg-brand-ink/5 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-brand-ink" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-brand-ink font-medium">{label}</p>
        <p className="text-xs text-brand-muted truncate">{desc}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-brand-tertiary group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all duration-200" />
    </Link>
  );
}

export default function LocationDetail() {
  const { loc } = useParams();
  const location = decodeURIComponent(loc || "");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const q = `?location=${encodeURIComponent(location)}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations/overview");
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const row = data?.locations?.find((l) => l.location === location);
  const canFin = data?.can_financials;
  const canPipeline = data?.can_pipeline;
  const accessible = !data || data.locations?.some((l) => l.location === location);

  const tiles = {
    overview: [
      canFin && { to: `/${q}`, icon: LineChart, label: "Financials", desc: "P&L, occupancy & trends", testid: "loc-tile-financials" },
      { to: `/sales${q}`, icon: TrendingUp, label: "Sales", desc: "Weekly & monthly performance", testid: "loc-tile-sales" },
    ].filter(Boolean),
    members: [
      { to: `/members${q}`, icon: IdCard, label: "Member Directory", desc: "Profiles, billing & access", testid: "loc-tile-members" },
      { to: `/bookings${q}`, icon: CalendarDays, label: "Room Bookings", desc: "Rooms & day passes", testid: "loc-tile-bookings" },
      { to: `/checkin-history${q}`, icon: History, label: "Day Pass Check-ins", desc: "Past day-pass visits", testid: "loc-tile-checkins" },
    ],
    sales: [
      { to: `/sales${q}`, icon: TrendingUp, label: "Sales Tracker", desc: "Weekly & monthly performance", testid: "loc-tile-sales" },
      { to: `/crm${q}`, icon: Contact, label: "CRM Pipeline", desc: "Leads & deals", testid: "loc-tile-crm" },
      { to: `/proposals${q}`, icon: FileSignature, label: "Proposals", desc: "Shareable pricing proposals", testid: "loc-tile-proposals" },
      canFin && { to: `/${q}`, icon: LineChart, label: "Financials", desc: "P&L, occupancy & trends", testid: "loc-tile-financials" },
    ].filter(Boolean),
    operations: [
      { to: `/ramp${q}`, icon: CreditCard, label: "Card Charges", desc: "Ramp expense coding", testid: "loc-tile-ramp" },
    ],
  };

  return (
    <div className="min-h-screen bg-brand-page">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <button onClick={() => navigate("/locations")} data-testid="loc-back"
          className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-ink transition-colors duration-200 mb-6">
          <ArrowLeft className="h-4 w-4" /> All locations
        </button>

        {!loading && !accessible ? (
          <div className="text-center py-24 text-brand-muted" data-testid="loc-no-access">
            You don't have access to this location.
          </div>
        ) : (
          <>
            <div className="relative rounded-2xl overflow-hidden mb-8 border border-brand-border h-48 sm:h-60">
              {row?.photo_url ? (
                <img src={mediaUrl(row.photo_url)} alt={location} className="h-full w-full object-cover" />
              ) : (
                <PhotoFallback label={location} testid="loc-detail-photo-fallback" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/90 via-brand-ink/40 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h1 className="font-serif text-4xl sm:text-5xl text-white tracking-tight drop-shadow-sm" data-testid="loc-detail-title">{location}</h1>
                {canFin && data?.period && row?.has_financials && (
                  <p className="text-sm text-white/85 mt-1">Financials as of {fmtPeriod(data.period)}</p>
                )}
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-xl bg-brand-surface border border-brand-border animate-pulse" />)}
              </div>
            ) : (
              <>
                {canPipeline && (
                  <div className="sticky top-16 z-20 -mx-6 lg:-mx-12 px-6 lg:px-12 mb-8 bg-brand-page/90 backdrop-blur-md border-b border-brand-border" data-testid="loc-tabs">
                    <div className="flex gap-1 overflow-x-auto">
                      {TABS.map(([key, label, Icon]) => (
                        <button key={key} onClick={() => setTab(key)} data-testid={`loc-tab-${key}`}
                          className={`inline-flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === key ? "border-brand-primary text-brand-ink font-medium" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>
                          <Icon className="h-4 w-4" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "overview" && (
                  <div className="space-y-12" data-testid="loc-panel-overview">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      {canFin && row?.has_financials && (
                        <>
                          <StatCard label="Total Revenue" value={fmtCurrency(row.total_revenue)} testid="loc-stat-revenue" />
                          <StatCard label="Net Income" value={fmtCurrency(row.net_income)}
                            tone={row.net_income < 0 ? "text-rose-600" : "text-brand-ink"} testid="loc-stat-netincome" />
                          <StatCard label="Occupancy" value={row.occupancy_pct == null ? "—" : `${(row.occupancy_pct * 100).toFixed(1)}%`} testid="loc-stat-occupancy" />
                        </>
                      )}
                    </div>
                    {canPipeline && (
                      <Section icon={DoorOpen} title="Availability & floor plan" sub="Live from Yardi" testid="loc-availability"
                        action={<Link to={`/availability${q}`} className="text-xs text-brand-primary inline-flex items-center gap-1" data-testid="loc-availability-open">Full availability <ArrowRight className="h-3 w-3" /></Link>}>
                        <AvailabilityPanel location={location} floorplanFirst />
                      </Section>
                    )}
                    {canPipeline && (
                      <Section icon={AlertTriangle} title="Needs attention" sub="Delinquent, on notice, or ending within 60 days" testid="loc-attention">
                        <NeedsAttention location={location} />
                      </Section>
                    )}
                    {!canPipeline && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="loc-modules">
                        {tiles.overview.map((t) => <ModuleTile key={t.testid} {...t} />)}
                      </div>
                    )}
                  </div>
                )}

                {tab === "members" && (
                  <div className="space-y-12" data-testid="loc-panel-members">
                    <Section icon={IdCard} title="Members" sub={`Search anyone at ${location}`} testid="loc-members">
                      <LocationMemberSearch location={location} />
                    </Section>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{tiles.members.map((t) => <ModuleTile key={t.testid} {...t} />)}</div>
                  </div>
                )}

                {tab === "sales" && (
                  <div className="space-y-8" data-testid="loc-panel-sales">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard label="Open Pipeline" value={fmtCurrency(row?.open_pipeline || 0)} tone="text-brand-gold" testid="loc-stat-pipeline" />
                      <StatCard label="Leads" value={row?.lead_count ?? 0} testid="loc-stat-leads" />
                      <StatCard label="Move-outs Logged" value={row?.moveouts_count ?? 0} tone={row?.moveouts_count ? "text-rose-600" : "text-brand-ink"} testid="loc-stat-moveouts" />
                      <StatCard label="Upcoming Day Passes" value={row?.upcoming_daypasses ?? 0} testid="loc-stat-daypasses" />
                    </div>
                    <div className="grid lg:grid-cols-2 gap-4">
                      <LocationMoveOuts location={location} />
                      <LocationWaitlist location={location} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{tiles.sales.map((t) => <ModuleTile key={t.testid} {...t} />)}</div>
                  </div>
                )}

                {tab === "events" && (
                  <div data-testid="loc-panel-events"><ProgrammingCalendar location={location} /></div>
                )}

                {tab === "operations" && (
                  <div className="space-y-12" data-testid="loc-panel-operations">
                    <Section icon={DoorClosed} title="Doors" testid="loc-doors"><DoorsPanel location={location} showHeaders={false} /></Section>
                    <Section icon={Video} title="Cameras" testid="loc-cameras"><CameraWall location={location} showLocationHeaders={false} /></Section>
                    <Section icon={Music2} title="Music" testid="loc-music"><MusicPanel location={location} showHeaders={false} /></Section>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{tiles.operations.map((t) => <ModuleTile key={t.testid} {...t} />)}</div>
                  </div>
                )}

                {tab === "tasks" && (
                  <div data-testid="loc-panel-tasks">
                    <Section icon={LayoutGrid} title="Tasks" testid="loc-tasks"><LocationBoards location={location} /></Section>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
